// **Die Formular-Reiter der Detailseite** (Kunde, Lizenz, Anmeldung,
// Endpoints, Integrationen) — die Schritte des Wizards (docs/11 11: eine
// Implementierung). „Speichern" ändert die Plattform; „Speichern und
// übernehmen" bringt es in die Instanz (Management-API, und die `.env` im
// Wartungsfenster).
//
// Anders als im Wizard stehen die Fehler sofort da: Ein eingerichteter
// Mandant mit Lücken soll sie zeigen, nicht verstecken.

import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ApiError, post, put } from "../../api/client";
import { useCatalog, useHost } from "../../api/queries";
import type { Checks, Run, RunResponse, StepCheck, StepSaveResponse, Tenant } from "../../api/types";
import { errorText, WIZARD_STEPS } from "../../lib/labels";
import { BusyButton } from "../../ui/busy-button";
import { toast } from "../../ui/toaster";
import { hasErrors, storeSavedStep } from "../steps/cache";
import { StepEditor } from "../steps/step-editor";
import type { StepBody } from "../steps/types";

export function FormTab({ tenant: initial, checks, step, onRunStarted }: {
  tenant: Tenant;
  checks: Checks;
  step: 1 | 2 | 3 | 4 | 5;
  onRunStarted: (run: Run) => void;
}) {
  const catalog = useCatalog();
  const client = useQueryClient();
  const [tenant, setTenant] = useState(initial);
  const [check, setCheck] = useState<StepCheck | null>(checks[String(step)] ?? null);
  // Nach „Speichern" neu aus dem gespeicherten Mandanten: Geheimnisse leer,
  // ihre Platzhalter sagen „gesetzt".
  const [version, setVersion] = useState(0);
  const host = useHost(false, step === 2).data ?? null;
  const active = tenant.state === "active";

  const saveStep = async (body: StepBody): Promise<StepCheck | null> => {
    try {
      const res = await put<StepSaveResponse>(`/tenants/${tenant.id}/steps/${step}`, body);
      setTenant(res.tenant);
      setCheck(res.check);
      storeSavedStep(client, tenant.id, step, res);
      if (hasErrors(res.check)) toast("Gespeichert, aber unvollständig", "error");
      return res.check;
    } catch (err) {
      if (err instanceof ApiError && err.body?.check) setCheck(err.body.check);
      toast(errorText(err), "error");
      return null;
    }
  };

  const save = async (collect: () => StepBody) => {
    const c = await saveStep(collect());
    if (!c) return;
    // Unvollständig steht schon als Meldung da — „Gespeichert" überschriebe sie.
    if (!hasErrors(c)) toast("Gespeichert");
    setVersion((v) => v + 1);
  };

  const apply = async (collect: () => StepBody) => {
    const c = await saveStep(collect());
    if (!c || hasErrors(c)) return;
    try {
      const res = await post<RunResponse>(`/tenants/${tenant.id}/apply`);
      onRunStarted(res.run);
      toast("Wird übernommen");
    } catch (err) { toast(errorText(err), "error"); }
  };

  return (
    <StepEditor
      key={version}
      step={step} tenant={tenant} catalog={catalog} check={check} host={host}
      title={WIZARD_STEPS[step - 1]}
      hint={active ? <>Speichern ändert die Plattform. <strong>Übernehmen</strong> bringt es in die Instanz — Env-Änderungen mit Neustart im Wartungsfenster.</> : undefined}
      onSave={async (body) => (await saveStep(body)) !== null}
      foot={(collect) => (
        <>
          <BusyButton onClick={() => save(collect)}>Speichern</BusyButton>
          {active && <BusyButton className="pf-btn pf-btn--primary" onClick={() => apply(collect)}>Speichern und übernehmen</BusyButton>}
        </>
      )}
    />
  );
}
